import { jsx, jsxs } from "react/jsx-runtime";
import {
  getSequenceDataBetweenRange,
  tidyUpSequenceData,
  getAminoAcidStringFromSequenceString,
  getReverseComplementAminoAcidStringFromSequenceString
} from "@teselagen/sequence-utils";
import { getSequenceWithinRange } from "@teselagen/range-utils";
import Clipboard from "clipboard";
import { compose } from "redux";
import {
  getReverseComplementSequenceAndAnnotations,
  getComplementSequenceAndAnnotations
} from "@teselagen/sequence-utils";
import { some, map, find, noop } from "lodash-es";
import { Menu, Spinner, Intent } from "@blueprintjs/core";
import { branch } from "recompose";
import { normalizePositionByRangeLength } from "@teselagen/range-utils";
import React from "react";
import Combokeys from "combokeys";
import {
  showContextMenu,
  showConfirmationDialog,
  commandMenuEnhancer,
  withStore
} from "@teselagen/ui";
import { jsonToGenbank } from "@teselagen/bio-parsers";
import withEditorProps from "../withEditorProps";
import getCommands from "../commands";
import moveCaret from "./moveCaret";
import createSequenceInputPopup from "./createSequenceInputPopup";
import Keyboard from "./Keyboard";
import {
  handleCaretMoved,
  editorClicked,
  updateSelectionOrCaret
} from "./clickAndDragUtils";
import getBpsPerRow from "./getBpsPerRow";
import {
  copyOptionsMenu,
  createNewAnnotationMenu
} from "../MenuBar/defaultConfig";
import { fullSequenceTranslationMenu } from "../MenuBar/viewSubmenu";
import { getNodeToRefocus, getSelFromWrappedAddon } from "../utils/editorUtils";
import {
  showAddOrEditAnnotationDialog,
  showDialog
} from "../GlobalDialogUtils";
import updateEditor from "../updateEditor";
const annotationClickHandlers = [
  "orfClicked",
  "primerClicked",
  "lineageAnnotationClicked",
  "assemblyPieceClicked",
  "translationClicked",
  "primaryProteinSequenceClicked",
  "cutsiteClicked",
  "translationDoubleClicked",
  "deletionLayerClicked",
  "replacementLayerClicked",
  "featureClicked",
  "warningClicked",
  "partClicked",
  "searchLayerClicked"
];
const ConnectedMenu = withEditorProps(({ children }) => /* @__PURE__ */ jsx(Menu, { children: children.map(React.cloneElement) }));
export function VectorInteractionHOC(Component) {
  return class VectorInteractionWrapper extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        isPasting: false
      };
      annotationClickHandlers.forEach((handler) => {
        this[handler] = (...args) => {
          const { clickOverrides = {} } = this.props;
          let preventDefault;
          const defaultHandler = this[handler + "_localOverride"] ? this[handler + "_localOverride"] : this.annotationClicked;
          if (clickOverrides[handler]) {
            preventDefault = clickOverrides[handler](...args);
          }
          !preventDefault && defaultHandler(...args);
        };
      });
      this.ConnectedMenu = (p) => {
        return /* @__PURE__ */ jsx(ConnectedMenu, { ...props, ...p });
      };
    }
    componentWillUnmount() {
      this.combokeys && this.combokeys.detach();
    }
    componentDidMount() {
      if (!this.node) return;
      this.combokeys = new Combokeys(this.node);
      this.combokeys.bind(
        "-.*ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split(""),
        (event) => {
          this.handleDnaInsert(event);
        }
      );
      const moveCaretBindings = [
        { keyCombo: ["left", "shift+left"], type: "moveCaretLeftOne" },
        { keyCombo: ["right", "shift+right"], type: "moveCaretRightOne" },
        { keyCombo: ["up", "shift+up"], type: "moveCaretUpARow" },
        { keyCombo: ["down", "shift+down"], type: "moveCaretDownARow" },
        {
          keyCombo: ["alt+right", "alt+shift+right"],
          type: "moveCaretToEndOfRow"
        },
        {
          keyCombo: ["alt+left", "alt+shift+left"],
          type: "moveCaretToStartOfRow"
        },
        {
          keyCombo: ["alt+up", "alt+shift+up"],
          type: "moveCaretToStartOfSequence"
        },
        {
          keyCombo: ["alt+down", "alt+shift+down"],
          type: "moveCaretToEndOfSequence"
        }
      ];
      moveCaretBindings.forEach(({ keyCombo, type }) => {
        this.combokeys.bind(keyCombo, (event) => {
          const shiftHeld = event.shiftKey;
          const bpsPerRow = getBpsPerRow(this.props);
          const {
            selectionLayer,
            caretPosition,
            sequenceLength,
            sequenceData: { isProtein, circular } = {},
            circular: circular2,
            caretPositionUpdate,
            selectionLayerUpdate
          } = this.props;
          const moveBy = moveCaret({
            sequenceLength,
            bpsPerRow,
            caretPosition,
            selectionLayer,
            isProtein,
            shiftHeld,
            type
          });
          handleCaretMoved({
            moveBy,
            circular: circular || circular2,
            sequenceLength,
            bpsPerRow,
            caretPosition,
            selectionLayer,
            shiftHeld,
            type,
            caretPositionUpdate,
            selectionLayerUpdate
          });
          event.stopPropagation();
        });
      });
      this.combokeys.bind("backspace", () => {
        this.handleDnaDelete({ isForwardDelete: false });
      });
      this.combokeys.bind("del", () => {
        this.handleDnaDelete({ isForwardDelete: true });
      });
      this.combokeys.bind("esc", (event) => {
        this.props.selectionLayerClear();
        this.props.caretPositionUpdate(-1);
        if (this.props.findTool && this.props.findTool.isOpen) {
          this.props.toggleFindTool();
        }
        event.stopPropagation();
      });
      this.commandEnhancer = commandMenuEnhancer(getCommands(this), {
        useTicks: true,
        omitIcons: true
      });
    }
    updateSelectionOrCaret = (shiftHeld, newRangeOrCaret) => {
      const {
        selectionLayer,
        caretPosition,
        sequenceData = { sequence: "" }
      } = this.props;
      const sequenceLength = sequenceData.sequence.length;
      updateSelectionOrCaret({
        doNotWrapOrigin: !sequenceData.circular,
        shiftHeld,
        sequenceLength,
        newRangeOrCaret,
        caretPosition,
        selectionLayer,
        selectionLayerUpdate: this.selectionLayerUpdate,
        caretPositionUpdate: this.caretPositionUpdate
      });
    };
    handlePaste = async (e) => {
      const {
        caretPosition = -1,
        selectionLayer = { start: -1, end: -1 },
        readOnly,
        onPaste,
        disableBpEditing,
        sequenceData,
        maxInsertSize,
        getAcceptedInsertChars
      } = this.props;
      if (disableBpEditing) {
        return this.createDisableBpEditingMsg();
      }
      if (readOnly) {
        return this.createReadOnlyMsg();
      }
      if (!(caretPosition > -1 || selectionLayer.start > -1)) {
        return window.toastr.warning("Please place the cursor before pasting");
      }
      e.preventDefault();
      let seqDataToInsert;
      try {
        if (onPaste) {
          seqDataToInsert = onPaste(e, this.props);
        } else {
          const clipboardData = e.clipboardData;
          let jsonData = clipboardData.getData("application/json");
          if (jsonData) {
            jsonData = JSON.parse(jsonData);
          }
          seqDataToInsert = jsonData || {
            sequence: clipboardData.getData("text/plain") || e.target.value
          };
        }
      } catch (err) {
        console.error("Paste parse failed: ", err);
        return window.toastr.error("Failed to parse pasted sequence");
      }
      if (sequenceData.isProtein) {
        seqDataToInsert.isProtein = true;
        if (!seqDataToInsert.proteinSequence) {
          seqDataToInsert.proteinSequence = seqDataToInsert.sequence;
        }
      }
      if (maxInsertSize && (seqDataToInsert.proteinSequence || seqDataToInsert.sequence).length > maxInsertSize) {
        return window.toastr.error(
          `Sorry, the pasted sequence exceeds the maximum allowed length of ${maxInsertSize}`
        );
      }
      const seqLen = (seqDataToInsert.sequence || seqDataToInsert.proteinSequence || "").length;
      const isLargePaste = seqLen > 1e3;
      if (isLargePaste) {
        this.setState({ isPasting: true });
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      try {
        seqDataToInsert = tidyUpSequenceData(seqDataToInsert, {
          topLevelSeqData: sequenceData,
          provideNewIdsForAnnotations: true,
          annotationsAsObjects: true,
          noCdsTranslations: true,
          getAcceptedInsertChars
        });
        if (!seqDataToInsert.sequence.length) {
          window.toastr.warning("Sorry no valid base pairs to paste");
          return;
        }
        await insertAndSelectHelper({
          seqDataToInsert,
          props: this.props
        });
        window.toastr.success("Sequence Pasted Successfully");
      } catch (err) {
        console.error("Paste failed: ", err);
        window.toastr.error("Failed to paste sequence");
      } finally {
        if (isLargePaste) {
          this.setState({ isPasting: false });
        }
      }
    };
    handleCutOrCopy = (_isCut) => async (e) => {
      const isCut = _isCut || this.isCut || false;
      e.preventDefault();
      const {
        onCopy = noop,
        sequenceData,
        selectionLayer,
        copyOptions,
        disableBpEditing,
        readOnly,
        getAcceptedInsertChars
      } = this.props;
      const onCut = this.props.onCut || this.props.onCopy || noop;
      let seqDataToCopy = this.sequenceDataToCopy || getSequenceDataBetweenRange(
        sequenceData,
        getSelFromWrappedAddon(
          selectionLayer,
          sequenceData.sequence.length
        ),
        {
          excludePartial: {
            features: !copyOptions.partialFeatures,
            parts: !copyOptions.partialParts
          },
          exclude: {
            features: !copyOptions.features,
            parts: !copyOptions.parts
          }
        }
      );
      if (!this.sequenceDataToCopy && selectionLayer.forward === false && !sequenceData.isProtein) {
        seqDataToCopy = getReverseComplementSequenceAndAnnotations(seqDataToCopy);
      }
      const seqData = tidyUpSequenceData(seqDataToCopy, {
        doNotRemoveInvalidChars: true,
        annotationsAsObjects: true,
        includeProteinSequence: true,
        getAcceptedInsertChars
      });
      if (!(this.sequenceDataToCopy || {}).textToCopy && !seqData.sequence.length)
        return window.toastr.warning(
          `No Sequence Selected To ${isCut && !(readOnly || disableBpEditing) ? "Cut" : "Copy"}`
        );
      const textToCopy = (this.sequenceDataToCopy || {}).textToCopy !== void 0 ? this.sequenceDataToCopy.textToCopy : seqData.isProtein ? seqData.proteinSequence : seqData.sequence;
      seqData.textToCopy = textToCopy;
      e.clipboardData.setData("text/plain", textToCopy);
      e.clipboardData.setData("application/json", JSON.stringify(seqData));
      e.preventDefault();
      if (isCut && !(readOnly || disableBpEditing) && !disableBpEditing) {
        this.handleDnaDelete(false);
        onCut(
          e,
          tidyUpSequenceData(seqData, {
            doNotRemoveInvalidChars: true,
            annotationsAsObjects: true,
            getAcceptedInsertChars
          }),
          this.props
        );
      } else {
        onCopy(e, seqData, this.props);
      }
      document.body.removeEventListener("cut", this.handleCut);
      document.body.removeEventListener("copy", this.handleCopy);
      window.toastr.success(
        `Selection ${isCut && !(readOnly || disableBpEditing) && !disableBpEditing ? "Cut" : "Copied"}`
      );
      this.sequenceDataToCopy = void 0;
    };
    handleCut = this.handleCutOrCopy(true);
    handleCopy = this.handleCutOrCopy();
    getDuplicateAction = () => {
      if (!this.props.onDuplicate) {
        return void 0;
      }
      return {
        action: {
          text: "Create a Duplicate?",
          onClick: () => {
            window.__tgClearAllToasts();
            this.props.onDuplicate(this.props.sequenceData, this.props);
          }
        }
      };
    };
    createDisableBpEditingMsg = () => {
      window.toastr.warning(
        typeof this.props.disableBpEditing === "string" ? this.props.disableBpEditing : "Sorry the underlying sequence is locked.",
        this.getDuplicateAction()
      );
    };
    createReadOnlyMsg = () => {
      window.toastr.warning(
        this.props.readOnly === "string" ? this.props.readOnly : "Sorry the sequence is Read-Only",
        this.getDuplicateAction()
      );
    };
    handleDnaInsert = async ({ useEventPositioning }) => {
      const {
        caretPosition = -1,
        selectionLayer = { start: -1, end: -1 },
        sequenceData = { sequence: "" },
        readOnly,
        disableBpEditing,
        maxInsertSize,
        getAcceptedInsertChars,
        showAminoAcidUnitAsCodon
      } = this.props;
      const sequenceLength = sequenceData.sequence.length;
      const isReplace = selectionLayer.start > -1;
      if (disableBpEditing) {
        return this.createDisableBpEditingMsg();
      }
      if (readOnly) {
        return this.createReadOnlyMsg();
      } else {
        createSequenceInputPopup({
          useEventPositioning,
          isReplace,
          sequenceData,
          isProtein: sequenceData.isProtein,
          selectionLayer,
          sequenceLength,
          caretPosition,
          maxInsertSize,
          getAcceptedInsertChars,
          showAminoAcidUnitAsCodon,
          handleInsert: async (seqDataToInsert) => {
            await insertAndSelectHelper({
              props: this.props,
              seqDataToInsert
            });
            window.toastr.success("Sequence Inserted Successfully");
          }
        });
      }
    };
    handleDnaDelete = async (options = {}) => {
      const {
        caretPosition = -1,
        selectionLayer = { start: -1, end: -1 },
        sequenceData = { sequence: "" },
        readOnly,
        disableBpEditing,
        updateSequenceData,
        wrappedInsertSequenceDataAtPositionOrRange,
        caretPositionUpdate
        // handleInsert
      } = this.props;
      let showToast = true;
      let isForwardDelete = false;
      if (typeof options === "boolean") {
        showToast = options;
      } else {
        showToast = options.showToast !== false;
        isForwardDelete = !!options.isForwardDelete;
      }
      const sequenceLength = sequenceData.sequence.length;
      if (disableBpEditing) {
        return this.createDisableBpEditingMsg();
      }
      if (readOnly) {
        return this.createReadOnlyMsg();
      }
      if (sequenceLength > 0) {
        let rangeToDelete = selectionLayer;
        let isCaretAtEndOfSeq;
        if (caretPosition > -1) {
          if (isForwardDelete) {
            if (caretPosition === sequenceLength) {
              return;
            }
            rangeToDelete = {
              start: caretPosition,
              end: normalizePositionByRangeLength(
                caretPosition + (sequenceData.isProtein ? 3 : 1) - 1,
                sequenceLength
              )
            };
          } else {
            if (caretPosition === 0) {
              return;
            }
            if (caretPosition === sequenceLength) {
              isCaretAtEndOfSeq = true;
            }
            rangeToDelete = {
              start: normalizePositionByRangeLength(
                caretPosition - (sequenceData.isProtein ? 3 : 1),
                sequenceLength
              ),
              end: normalizePositionByRangeLength(
                caretPosition - 1,
                sequenceLength
              )
            };
          }
        } else {
          if (rangeToDelete.end === sequenceLength - 1) {
            isCaretAtEndOfSeq = true;
          }
        }
        const [newSeqData, { abortSeqChange }] = await wrappedInsertSequenceDataAtPositionOrRange(
          {},
          sequenceData,
          rangeToDelete
        );
        if (abortSeqChange) return;
        updateSequenceData(newSeqData);
        caretPositionUpdate(
          isCaretAtEndOfSeq ? newSeqData.sequence.length : rangeToDelete.start > newSeqData.sequence.length ? (
            //we're deleting around the origin so set the cursor to the 0 position
            0
          ) : normalizePositionByRangeLength(
            rangeToDelete.start,
            newSeqData.sequence.length
          )
        );
        if (showToast) window.toastr.success("Sequence Deleted Successfully");
      }
    };
    caretPositionUpdate = (position) => {
      const { caretPosition = -1 } = this.props;
      if (caretPosition === position) {
        return;
      }
      this.props.caretPositionUpdate(position);
    };
    selectionLayerUpdate = (newSelection) => {
      const { selectionLayer = { start: -1, end: -1 }, ignoreGapsOnHighlight } = this.props;
      if (!newSelection) return;
      const { start, end, forceUpdate } = newSelection;
      if (selectionLayer.start === start && selectionLayer.end === end && selectionLayer.forceUpdate === forceUpdate) {
        return;
      }
      this.props.selectionLayerUpdate({
        ...newSelection,
        start,
        end,
        ignoreGaps: ignoreGapsOnHighlight
      });
    };
    annotationClicked = ({ event, annotation }) => {
      if (event.target) {
        event.target?.closest(".veVectorInteractionWrapper")?.focus();
      }
      event.preventDefault && event.preventDefault();
      event.stopPropagation && event.stopPropagation();
      const {
        annotationSelect,
        selectionLayer,
        annotationDeselectAll,
        propertiesViewTabUpdate
      } = this.props;
      let forceUpdate;
      if (annotation.start > -1 && // selectionLayer.start === annotation.start &&
      // selectionLayer.end === annotation.end &&
      event.altKey) {
        forceUpdate = selectionLayer.forceUpdate === "end" ? "start" : "end";
      }
      this.updateSelectionOrCaret(event.shiftKey || event.metaKey, {
        ...annotation,
        isFromRowView: !!event?.target?.closest(".veRowView"),
        ...forceUpdate && { forceUpdate }
      });
      !event.shiftKey && annotationDeselectAll(void 0);
      annotation.id && annotationSelect(annotation);
      annotation.annotationTypePlural && propertiesViewTabUpdate(annotation.annotationTypePlural, annotation);
    };
    cutsiteClicked_localOverride = ({ event, annotation }) => {
      event.preventDefault();
      event.stopPropagation();
      const { annotationSelect, annotationDeselectAll } = this.props;
      this.updateSelectionOrCaret(event.shiftKey, {
        start: annotation.recognitionSiteRange.start,
        end: annotation.recognitionSiteRange.end
      });
      annotationDeselectAll(void 0);
      annotationSelect(annotation);
    };
    warningDoubleClicked = ({ event, annotation, doNotStopPropagation }) => {
      if (!doNotStopPropagation) {
        event.preventDefault();
        event.stopPropagation();
      }
      const { annotationSelect, annotationDeselectAll } = this.props;
      showConfirmationDialog({
        cancelButtonText: null,
        confirmButtonText: "OK",
        canEscapeKeyCancel: true,
        // intent: Intent.NONE,
        // onCancel: undefined,
        text: /* @__PURE__ */ jsxs("div", { style: { wordBreak: "break-word" }, children: [
          /* @__PURE__ */ jsxs("h3", { children: [
            annotation.name,
            ":"
          ] }),
          annotation.message
        ] })
      });
      this.updateSelectionOrCaret(event.shiftKey, annotation);
      annotationDeselectAll(void 0);
      annotationSelect(annotation);
    };
    insertHelper = {
      onClick: (e, ctxInfo) => {
        this.handleDnaInsert({
          useEventPositioning: {
            e,
            nodeToReFocus: getNodeToRefocus(ctxInfo.event.target)
          }
        });
      }
    };
    makeTextCopyable = (transformFunc, className, action = "copy") => {
      return new Clipboard(`.${className}`, {
        action: () => action,
        text: () => {
          this.isCut = action === "cut";
          const { editorName, store } = this.props;
          const { sequenceData, copyOptions, selectionLayer } = store.getState().VectorEditor[editorName];
          const selectedSeqData = getSequenceDataBetweenRange(
            sequenceData,
            getSelFromWrappedAddon(
              selectionLayer,
              sequenceData.sequence.length
            ),
            {
              excludePartial: {
                features: !copyOptions.partialFeatures,
                parts: !copyOptions.partialParts
              },
              exclude: {
                features: !copyOptions.features,
                parts: !copyOptions.parts
              }
            }
          );
          const sequenceDataToCopy = transformFunc(
            selectedSeqData,
            sequenceData
          );
          this.sequenceDataToCopy = sequenceDataToCopy;
          if (window.Cypress) {
            window.Cypress.textToCopy = sequenceDataToCopy.textToCopy;
            window.Cypress.seqDataToCopy = sequenceDataToCopy;
          }
          return sequenceDataToCopy.textToCopy || sequenceDataToCopy.sequence;
        }
      });
    };
    getAACopyOption = (annotation) => {
      const { sequenceData } = this.props;
      const { isProtein } = sequenceData || {};
      const getAACopyText = (selectedSeqData) => {
        let forward = annotation?.forward;
        if (forward === void 0) {
          const matchingFeat = Object.values(
            this.props.sequenceData?.features || {}
          ).find(
            (f) => (f.type?.toLowerCase() === "cds" || f.isCds) && f.start === annotation?.start && f.end === annotation?.end
          );
          if (matchingFeat) {
            forward = matchingFeat.forward;
          }
        }
        return isProtein ? selectedSeqData.proteinSequence.toUpperCase() : forward === false ? getReverseComplementAminoAcidStringFromSequenceString(
          selectedSeqData.sequence
        ) : getAminoAcidStringFromSequenceString(
          selectedSeqData.sequence
        );
      };
      return {
        text: "Copy AA Sequence",
        className: "openVeCopyAA",
        willUnmount: () => {
          this.openVeCopyAA && this.openVeCopyAA.destroy();
        },
        didMount: ({ className }) => {
          this.openVeCopyAA = this.makeTextCopyable((selectedSeqData) => {
            const textToCopy = getAACopyText(selectedSeqData);
            return {
              ...selectedSeqData,
              textToCopy
            };
          }, className);
        },
        onClick: async () => {
          try {
            const { editorName, store } = this.props;
            const state = store.getState().VectorEditor[editorName];
            const currentSeqData = state.sequenceData;
            const copyOptions = state.copyOptions;
            const selectionLayer = state.selectionLayer;
            const selectedSeqData = getSequenceDataBetweenRange(
              currentSeqData,
              getSelFromWrappedAddon(
                selectionLayer,
                currentSeqData.sequence.length
              ),
              {
                excludePartial: {
                  features: !copyOptions.partialFeatures,
                  parts: !copyOptions.partialParts
                },
                exclude: {
                  features: !copyOptions.features,
                  parts: !copyOptions.parts
                }
              }
            );
            const textToCopy = getAACopyText(selectedSeqData);
            if (navigator?.clipboard?.writeText) {
              await navigator.clipboard.writeText(textToCopy);
            }
            window.toastr?.success && window.toastr.success("Amino acid sequence copied to clipboard");
          } catch (e) {
            console.error("Error copying AA sequence:", e);
          }
        }
      };
    };
    getCopyOptions = (annotation) => {
      const { sequenceData, readOnly, disableBpEditing, selectionLayer } = this.props;
      const { isProtein } = sequenceData;
      document.body.addEventListener("copy", this.handleCopy, { once: true });
      document.body.addEventListener("cut", this.handleCut, { once: true });
      const aaCopy = this.getAACopyOption(annotation);
      return [
        ...readOnly || disableBpEditing ? [] : [
          {
            text: "Replace",
            ...this.insertHelper
          },
          {
            text: "Cut",
            className: "openVeCut",
            willUnmount: () => {
              this.openVeCut && this.openVeCut.destroy();
            },
            didMount: ({ className }) => {
              this.openVeCut = this.makeTextCopyable(
                (s) => ({
                  ...s,
                  textToCopy: isProtein ? s.proteinSequence : s.sequence
                }),
                className,
                "cut"
              );
            }
          }
        ],
        {
          text: annotation.overlapsSelf || selectionLayer.overlapsSelf ? "Copy Wrapped Range" : "Copy",
          submenu: [
            ...isProtein ? [aaCopy] : [],
            {
              text: isProtein ? "Copy DNA Bps" : "Copy",
              className: "openVeCopy2",
              willUnmount: () => {
                this.openVeCopy2 && this.openVeCopy2.destroy();
              },
              didMount: ({ className }) => {
                this.openVeCopy2 = this.makeTextCopyable(
                  (s) => ({ ...s, textToCopy: s.sequence }),
                  className
                );
              }
            },
            {
              text: "Copy Genbank For Selection",
              className: "openVeCopyGenbankForSelection",
              willUnmount: () => {
                this.openVeCopyGenbankForSelection && this.openVeCopyGenbankForSelection.destroy();
              },
              didMount: ({ className }) => {
                this.openVeCopyGenbankForSelection = this.makeTextCopyable(
                  getGenbankFromSelection,
                  className
                );
              }
            },
            {
              text: isProtein ? "Copy Reverse Complement DNA Bps" : "Copy Reverse Complement",
              className: "openVeCopyReverse",
              willUnmount: () => {
                this.openVeCopyReverse && this.openVeCopyReverse.destroy();
              },
              didMount: ({ className }) => {
                this.openVeCopyReverse = this.makeTextCopyable(
                  getReverseComplementSequenceAndAnnotations,
                  className
                );
              }
            },
            ...isProtein ? [] : [aaCopy],
            {
              text: "Copy Reverse Complement AA Sequence",
              className: "openVeCopyAAReverse",
              willUnmount: () => {
                this.openVeCopyAAReverse && this.openVeCopyAAReverse.destroy();
              },
              didMount: ({ className }) => {
                this.openVeCopyAAReverse = this.makeTextCopyable((selectedSeqData) => {
                  const revSeqData = getReverseComplementSequenceAndAnnotations(selectedSeqData);
                  const textToCopy = isProtein ? revSeqData.proteinSequence.toUpperCase() : getAminoAcidStringFromSequenceString(revSeqData.sequence);
                  return {
                    ...revSeqData,
                    textToCopy
                  };
                }, className);
              }
            },
            {
              text: isProtein ? "Copy Complement DNA Bps" : "Copy Complement",
              className: "openVeCopyComplement",
              willUnmount: () => {
                this.openVeCopyComplement && this.openVeCopyComplement.destroy();
              },
              didMount: ({ className }) => {
                this.openVeCopyComplement = this.makeTextCopyable(
                  getComplementSequenceAndAnnotations,
                  className
                );
              }
            },
            copyOptionsMenu
          ]
        }
      ];
    };
    getSelectionMenuOptions = (annotation) => {
      const allCopyOptions = this.getCopyOptions(annotation);
      const isCopy = (i) => ["Copy", "Copy Wrapped Range"].includes(i.text);
      const copyOnly = allCopyOptions.filter(isCopy);
      const editOnly = allCopyOptions.filter((i) => !isCopy(i));
      const items = [
        {
          text: "Create & Export",
          submenu: ["newFeature", "newPrimer", "newPart"]
        },
        {
          text: "Edit DNA",
          submenu: [
            ...editOnly,
            {
              cmd: "changeCaseCmd",
              text: "Change Case",
              submenu: ["upperCaseSelection", "lowerCaseSelection"]
            }
          ]
        },
        {
          text: "Selection",
          submenu: [
            ...copyOnly,
            "selectInverse",
            "reverseComplementSelection",
            "complementSelection"
          ]
        }
      ];
      return items;
    };
    normalizeAction = ({ event, ...rest }, handler) => {
      event.preventDefault();
      event.stopPropagation();
      return handler({ event, ...rest }, this.props);
    };
    enhanceRightClickAction = (action, key) => {
      return (opts) => {
        const { rightClickOverrides = {} } = this.props;
        const items = action(opts);
        const e = items && items._event || opts.event || opts;
        if (e.target) {
          e.target?.closest(".veVectorInteractionWrapper")?.focus();
        }
        const lastFocusedEl = document.activeElement;
        e.preventDefault && e.preventDefault();
        e.stopPropagation && e.stopPropagation();
        const override = rightClickOverrides[key];
        showContextMenu(
          override ? override(items, opts, this.props) : items,
          [this.commandEnhancer],
          e,
          () => {
            document.body.removeEventListener("cut", this.handleCut);
            document.body.removeEventListener("copy", this.handleCopy);
            if (lastFocusedEl) {
              lastFocusedEl.focus();
            }
          },
          opts,
          // context here
          this.ConnectedMenu
        );
      };
    };
    selectionLayerRightClicked = this.enhanceRightClickAction(
      ({ annotation }) => {
        const { sequenceData } = this.props;
        const allFeatures = Object.values(sequenceData?.features || {});
        const matchingCds = allFeatures.find(
          (f) => (f.type?.toLowerCase() === "cds" || f.isCds) && f.start === annotation.start && f.end === annotation.end
        );
        const targetAnnotation = matchingCds ? { ...annotation, forward: matchingCds.forward, type: "CDS", isCds: true } : annotation;
        const selectionOptions = this.getSelectionMenuOptions({
          isWrappedAddon: targetAnnotation.isWrappedAddon,
          overlapsSelf: targetAnnotation.overlapsSelf,
          start: targetAnnotation.start,
          end: targetAnnotation.end
        });
        if (matchingCds) {
          const aaCopyOption = this.getAACopyOption(targetAnnotation);
          if (aaCopyOption) {
            return [aaCopyOption, "--", ...selectionOptions];
          }
        }
        return selectionOptions;
      },
      "selectionLayerRightClicked"
    );
    digestLaneRightClicked = this.enhanceRightClickAction(() => {
      return ["newFeature", "newPart"];
    }, "digestLaneRightClicked");
    searchLayerRightClicked = this.enhanceRightClickAction(({ annotation }) => {
      this.props.selectionLayerUpdate({
        start: annotation.start,
        end: annotation.end,
        forward: !annotation.bottomStrand
      });
      return this.getSelectionMenuOptions({
        //manually only pluck off the start and end so that if the selection layer was generated from say a feature, those properties won't be carried into the create part/feature/primer dialogs
        start: annotation.start,
        end: annotation.end,
        forward: !annotation.bottomStrand
      });
    }, "searchLayerRightClicked");
    backgroundRightClicked = this.enhanceRightClickAction(
      ({ nearestCaretPos, shiftHeld, event }) => {
        this.updateSelectionOrCaret(shiftHeld, nearestCaretPos);
        const {
          readOnly,
          disableBpEditing
          // sequenceData: { circular }
        } = this.props;
        const menu = [
          ...readOnly || disableBpEditing ? [] : [
            {
              text: "Insert",
              ...this.insertHelper
            }
          ],
          "rotateToCaretPosition",
          "reverseComplementEntireSequence",
          "createMenuHolder",
          {
            ...fullSequenceTranslationMenu,
            text: "View Full Sequence Translations"
          }
        ];
        menu._event = event;
        return menu;
      },
      "backgroundRightClicked"
    );
    deletionLayerRightClicked = this.enhanceRightClickAction(
      ({ annotation }) => {
        const { editorName, dispatch } = this.props;
        return [
          {
            text: "Remove Deletion",
            // icon: "ion-plus-round",
            onClick: function() {
              dispatch({
                type: "DELETION_LAYER_DELETE",
                meta: { editorName },
                payload: { ...annotation }
              });
            }
          }
        ];
      },
      "deletionLayerRightClicked"
    );
    partRightClicked = this.enhanceRightClickAction(({ annotation, event }) => {
      this.props.selectionLayerUpdate({
        isFromRowView: !!event?.target?.closest(".veRowView"),
        start: annotation.start,
        end: annotation.end,
        isWrappedAddon: annotation.isWrappedAddon,
        overlapsSelf: annotation.overlapsSelf
      });
      return [
        ...annotation.isPreview ? [
          {
            text: "Apply Part",
            onClick: () => {
              const updatedPart = {
                ...annotation,
                isPreview: false,
                opacity: 1,
                name: annotation.name.replace(/ \(\d+% match\)$/, "")
              };
              this.props.upsertPart(updatedPart);
            }
          },
          "--"
        ] : [],
        {
          text: "Part Management",
          submenu: [
            ...getEditDeleteHandlers("Part", annotation),
            "showRemoveDuplicatesDialogParts"
          ]
        },
        "--",
        ...this.getSelectionMenuOptions(annotation),
        "--",
        "viewPartProperties"
      ];
    }, "partRightClicked");
    warningRightClicked = this.enhanceRightClickAction(
      ({ annotation, event }) => {
        this.props.selectionLayerUpdate({
          isFromRowView: !!event?.target?.closest(".veRowView"),
          start: annotation.start,
          end: annotation.end
        });
        return [
          {
            text: "View Warning Details",
            onClick: (event2) => {
              this.warningDoubleClicked({
                event: event2,
                annotation,
                doNotStopPropagation: true
              });
            }
          },
          "--",
          ...this.getSelectionMenuOptions(annotation)
        ];
      },
      "warningRightClicked"
    );
    featureRightClicked = this.enhanceRightClickAction(
      ({ annotation, event }) => {
        this.props.selectionLayerUpdate({
          isFromRowView: !!event?.target?.closest(".veRowView"),
          start: annotation.start,
          end: annotation.end
        });
        event.persist();
        const { readOnly, annotationsToSupport: { parts } = {} } = this.props;
        const isCds = annotation?.type?.toLowerCase() === "cds" || annotation?.isCds;
        const aaCopyOption = this.getAACopyOption(annotation);
        return [
          ...isCds && aaCopyOption ? [aaCopyOption, "--"] : [],
          ...annotation.isPreview ? [
            {
              text: "Apply Feature",
              onClick: () => {
                const updatedFeature = {
                  ...annotation,
                  isPreview: false,
                  opacity: 1,
                  name: annotation.name.replace(/ \(\d+% match\)$/, "")
                };
                this.props.upsertFeature(updatedFeature);
              }
            }
          ] : [],
          {
            text: "Feature Management",
            submenu: [
              ...annotation.type === "restriction_site" && !readOnly ? [
                {
                  text: "Unmark important",
                  onClick: () => {
                    this.props.deleteFeature(annotation.id);
                    updateEditor(this.props.store, this.props.editorName, {
                      annotationVisibility: {
                        ...this.props.annotationVisibility,
                        features: true
                      }
                    });
                  }
                },
                "--"
              ] : [],
              ...getEditDeleteHandlers("Feature", annotation),
              ...annotation.type === "s_mutation" && /^[ACTG]>[ACTG]$/i.test(annotation.name) ? [
                {
                  text: "Undo Mutation",
                  onClick: () => {
                    const {
                      sequenceData,
                      updateSequenceData,
                      deleteFeature
                    } = this.props;
                    const [oldBase] = annotation.name.split(">");
                    const newSeq = sequenceData.sequence.slice(0, annotation.start) + oldBase + sequenceData.sequence.slice(annotation.start + 1);
                    updateSequenceData({
                      ...sequenceData,
                      sequence: newSeq
                    });
                    deleteFeature([annotation.id]);
                  }
                }
              ] : [],
              ...annotation.fileSource ? [
                {
                  text: "Go to Feature Source",
                  onClick: async () => {
                    try {
                      const token = localStorage.getItem("ove_token");
                      const response = await fetch(
                        `/api/file/find/${encodeURIComponent(
                          annotation.fileSource
                        )}`,
                        {
                          headers: {
                            ...token && { "Authorization": `Bearer ${token}` }
                          }
                        }
                      );
                      const data = await response.json();
                      if (data && data.path) {
                        const currentHash = window.location.hash || "#/Standalone";
                        const parts2 = currentHash.split("?");
                        const baseHash = parts2[0];
                        const params = new URLSearchParams(parts2[1] || "");
                        params.set("file", data.path);
                        window.location.hash = `${baseHash}?${params.toString()}`;
                      } else {
                        window.toastr.error(
                          `Could not find source file: ${annotation.fileSource}`
                        );
                      }
                    } catch (e) {
                      window.toastr.error(
                        `Error finding source file: ${e.message}`
                      );
                    }
                  }
                }
              ] : [],
              ...readOnly ? [] : [
                ...parts ? [
                  {
                    text: "Make a Part from Feature",
                    onClick: async () => {
                      const { sequenceData, upsertPart } = this.props;
                      if (some(sequenceData.parts, (part) => {
                        if (part.start === annotation.start && part.end === annotation.end) {
                          return true;
                        }
                      })) {
                        const doAction = await showConfirmationDialog({
                          text: "A part already exists that matches this feature's range. Do you want to make one anyways?",
                          confirmButtonText: "Create Part",
                          canEscapeKeyCancel: true
                          //this is false by default
                        });
                        if (!doAction) return;
                      }
                      upsertPart({
                        start: annotation.start,
                        end: annotation.end,
                        type: annotation.type,
                        forward: annotation.forward,
                        name: annotation.name
                      });
                    }
                  }
                ] : [],
                {
                  text: "Merge With Another Feature",
                  onClick: () => {
                    this.annotationClicked({
                      annotation,
                      event: { ...event, shiftHeld: true }
                    });
                    showDialog({
                      dialogType: "MergeFeaturesDialog"
                    });
                  }
                },
                "showRemoveDuplicatesDialogFeatures",
                {
                  text: `Delete all Type "${annotation.type}"`,
                  intent: "danger",
                  onClick: async () => {
                    const { sequenceData, deleteFeature } = this.props;
                    const allFeatures = Object.values(
                      sequenceData.features || {}
                    );
                    const sameType = allFeatures.filter(
                      (f) => f.type === annotation.type && !f.isEditLocked
                    );
                    if (!sameType.length) return;
                    const confirmed = await showConfirmationDialog({
                      intent: "danger",
                      confirmButtonText: `Delete ${sameType.length} Feature${sameType.length > 1 ? "s" : ""}`,
                      canEscapeKeyCancel: true,
                      text: `This will permanently delete all ${sameType.length} feature${sameType.length > 1 ? "s" : ""} of type "${annotation.type}". This action can be undone.`
                    });
                    if (!confirmed) return;
                    deleteFeature(sameType.map((f) => f.id));
                  }
                }
              ],
              "toggleCdsFeatureTranslations"
            ]
          },
          ...this.getSelectionMenuOptions(annotation),
          {
            text: "Properties",
            submenu: [
              "viewFeatureProperties"
            ]
          }
        ];
      },
      "featureRightClicked"
    );
    cutsiteRightClicked = this.enhanceRightClickAction(({ annotation }) => {
      const { upsertFeature, readOnly } = this.props;
      return [
        ...readOnly ? [] : annotation.isImportant ? [
          {
            text: "Unmark important",
            onClick: () => {
              this.props.deleteFeature(annotation.importantFeatureId);
              updateEditor(this.props.store, this.props.editorName, {
                annotationVisibility: {
                  ...this.props.annotationVisibility,
                  features: true
                }
              });
            }
          },
          "--"
        ] : [
          {
            text: "Mark as Important",
            onClick: () => {
              const feat = {
                name: annotation.restrictionEnzyme.name,
                type: "restriction_site",
                start: annotation.recognitionSiteRange.start,
                end: annotation.recognitionSiteRange.end,
                topSnipPosition: annotation.topSnipPosition,
                bottomSnipPosition: annotation.bottomSnipPosition,
                forward: annotation.forward,
                color: "black",
                labelColor: "black",
                notes: {
                  restrictionEnzyme: [
                    JSON.stringify(annotation.restrictionEnzyme)
                  ]
                }
              };
              upsertFeature(feat);
              updateEditor(this.props.store, this.props.editorName, {
                annotationVisibility: {
                  ...this.props.annotationVisibility,
                  features: true
                }
              });
            }
          },
          "--"
        ],
        "viewCutsiteProperties"
      ];
    }, "cutsiteRightClicked");
    primerRightClicked = this.enhanceRightClickAction(
      ({ annotation, event }) => {
        this.props.selectionLayerUpdate({
          isFromRowView: !!event?.target?.closest(".veRowView"),
          start: annotation.start,
          end: annotation.end,
          isWrappedAddon: annotation.isWrappedAddon,
          overlapsSelf: annotation.overlapsSelf
        });
        const {
          sequenceData,
          upsertPrimer,
          createNewPCR,
          selectionLayerUpdate,
          readOnly,
          deletePrimer,
          upsertPart,
          annotationsToSupport: { parts } = {}
        } = this.props;
        const primerPairId = annotation.primerId;
        let primerPairSubmenu = [];
        if (primerPairId) {
          const pairedPrimer = find(
            sequenceData.primers,
            (p) => p.primerId === primerPairId && p.id !== annotation.id
          );
          if (pairedPrimer) {
            primerPairSubmenu = [
              {
                text: "Select PCR Product",
                onClick: () => {
                  selectionLayerUpdate({
                    start: annotation.forward ? annotation.start : pairedPrimer.start,
                    end: annotation.forward ? pairedPrimer.end : annotation.end
                  });
                }
              },
              {
                text: "Simulate PCR with Pair",
                onClick: () => {
                  createNewPCR();
                }
              }
            ];
          }
        }
        return [
          ...annotation.isPreview ? [
            {
              text: "Apply Primer",
              onClick: () => {
                const updatedPrimer = {
                  ...annotation,
                  isPreview: false,
                  opacity: 1,
                  name: annotation.name.replace(/ \(\d+% match\)$/, "")
                };
                this.props.upsertPrimer(updatedPrimer);
              }
            },
            "--"
          ] : [],
          {
            text: "Feature Management",
            submenu: [
              ...getEditDeleteHandlers("Primer", annotation),
              ...annotation.fileSource ? [
                {
                  text: "Go to Primer Source",
                  onClick: async () => {
                    try {
                      const token = localStorage.getItem("ove_token");
                      const response = await fetch(
                        `/api/file/find/${encodeURIComponent(
                          annotation.fileSource
                        )}`,
                        {
                          headers: {
                            ...token && {
                              Authorization: `Bearer ${token}`
                            }
                          }
                        }
                      );
                      const data = await response.json();
                      if (data && data.path) {
                        const currentHash = window.location.hash || "#/Standalone";
                        const parts2 = currentHash.split("?");
                        const baseHash = parts2[0];
                        const params = new URLSearchParams(parts2[1] || "");
                        params.set("file", data.path);
                        window.location.hash = `${baseHash}?${params.toString()}`;
                      } else {
                        window.toastr.error(
                          `Could not find source file: ${annotation.fileSource}`
                        );
                      }
                    } catch (e) {
                      window.toastr.error(
                        `Error finding source file: ${e.message}`
                      );
                    }
                  }
                }
              ] : [],
              ...readOnly ? [] : [
                ...parts ? [
                  {
                    text: "Make a Part from Primer",
                    onClick: async () => {
                      const { sequenceData: sequenceData2, upsertPart: upsertPart2 } = this.props;
                      if (some(sequenceData2.parts, (part) => {
                        if (part.start === annotation.start && part.end === annotation.end) {
                          return true;
                        }
                      })) {
                        const doAction = await showConfirmationDialog({
                          text: "A part already exists that matches this primer's range. Do you want to make one anyways?",
                          confirmButtonText: "Create Part",
                          canEscapeKeyCancel: true
                          //this is false by default
                        });
                        if (!doAction) return;
                      }
                      upsertPart2({
                        start: annotation.start,
                        end: annotation.end,
                        type: "primer",
                        forward: annotation.forward,
                        name: annotation.name
                      });
                    }
                  }
                ] : [],
                {
                  text: "Merge With Another Primer",
                  onClick: () => {
                    this.annotationClicked({
                      annotation,
                      event: { ...event, shiftHeld: true }
                    });
                    showDialog({
                      dialogType: "MergeFeaturesDialog"
                    });
                  }
                },
                "showRemoveDuplicatesDialogPrimers",
                {
                  text: `Delete all Type "${annotation.type}"`,
                  intent: "danger",
                  onClick: async () => {
                    const allPrimers = Object.values(
                      sequenceData.primers || {}
                    );
                    const sameType = allPrimers.filter(
                      (f) => f.type === annotation.type && !f.isEditLocked
                    );
                    if (!sameType.length) return;
                    const confirmed = await showConfirmationDialog({
                      intent: "danger",
                      confirmButtonText: `Delete ${sameType.length} Primer${sameType.length > 1 ? "s" : ""}`,
                      canEscapeKeyCancel: true,
                      text: `This will permanently delete all ${sameType.length} primer${sameType.length > 1 ? "s" : ""} of type "${annotation.type}". This action can be undone.`
                    });
                    if (!confirmed) return;
                    deletePrimer(sameType.map((f) => f.id));
                  }
                }
              ]
            ]
          },
          ...primerPairSubmenu.length ? [
            {
              text: "Primer Pair",
              submenu: primerPairSubmenu
            },
            "--"
          ] : [],
          ...this.getSelectionMenuOptions(annotation),
          {
            text: "Properties",
            submenu: ["viewPrimerProperties"]
          }
        ];
      },
      "primerRightClicked"
    );
    orfRightClicked = this.enhanceRightClickAction(({ annotation, event }) => {
      this.props.selectionLayerUpdate({
        isFromRowView: !!event?.target?.closest(".veRowView"),
        start: annotation.start,
        end: annotation.end
      });
      const aaCopyOption = this.getAACopyOption(annotation);
      return [
        ...aaCopyOption ? [aaCopyOption, "--"] : [],
        "toggleOrfTranslations",
        ...this.getSelectionMenuOptions(annotation),
        "viewOrfProperties"
      ];
    }, "orfRightClicked");
    translationRightClicked = this.enhanceRightClickAction(
      ({ event, annotation }) => {
        event.preventDefault();
        event.stopPropagation();
        const { annotationVisibilityToggle } = this.props;
        this.props.selectionLayerUpdate({
          isFromRowView: !!event?.target?.closest(".veRowView"),
          start: annotation.start,
          end: annotation.end
        });
        if (annotation.isOrf) {
          return [
            {
              text: "Hide ORF Translations",
              onClick: () => {
                annotationVisibilityToggle("orfTranslations");
              }
            },
            "viewOrfProperties"
          ];
        }
        const aaCopyOption = this.getAACopyOption(annotation);
        return [
          ...aaCopyOption ? [aaCopyOption, "--"] : [],
          "deleteTranslation",
          ...this.getSelectionMenuOptions(annotation),
          "viewTranslationProperties"
        ];
      },
      "translationRightClicked"
    );
    featureDoubleClicked = ({ event, annotation }) => {
      const { doubleClickOverrides = {} } = this.props;
      let preventDefault;
      if (doubleClickOverrides["featureDoubleClicked"]) {
        preventDefault = doubleClickOverrides["featureDoubleClicked"]({
          annotation,
          event
        });
      }
      if (!preventDefault) {
        event.preventDefault();
        event.stopPropagation();
        showAddOrEditAnnotationDialog({ type: "feature", annotation });
      }
    };
    partDoubleClicked = ({ event, annotation }) => {
      const { doubleClickOverrides = {} } = this.props;
      let preventDefault;
      if (doubleClickOverrides["partDoubleClicked"]) {
        preventDefault = doubleClickOverrides["partDoubleClicked"]({
          annotation,
          event
        });
      }
      if (!preventDefault) {
        event.preventDefault();
        event.stopPropagation();
        showAddOrEditAnnotationDialog({ type: "part", annotation });
      }
    };
    primerDoubleClicked = ({ event, annotation }) => {
      const { doubleClickOverrides = {} } = this.props;
      let preventDefault;
      if (doubleClickOverrides["primerDoubleClicked"]) {
        preventDefault = doubleClickOverrides["primerDoubleClicked"]({
          annotation,
          event
        });
      }
      if (!preventDefault) {
        event.preventDefault();
        event.stopPropagation();
        showAddOrEditAnnotationDialog({ type: "primer", annotation });
      }
    };
    cutsiteDoubleClicked = ({ annotation }) => {
      showDialog({
        dialogType: "AdditionalCutsiteInfoDialog",
        props: {
          dialogProps: {
            title: annotation.name
          },
          cutsiteOrGroupKey: annotation.name
        }
      });
    };
    render() {
      const {
        closePanelButton,
        selectionLayer = { start: -1, end: -1 },
        sequenceData = { sequence: "" },
        tabHeight
        //height of the little clickable tabs (passed because they are measured together with the editor panels and thus need to be subtracted)
        // fitHeight //used to allow the editor to expand to fill the height of its containing component
      } = this.props;
      let {
        // eslint-disable-next-line prefer-const
        children,
        // eslint-disable-next-line prefer-const
        vectorInteractionWrapperStyle = {},
        // eslint-disable-next-line prefer-const
        disableEditorClickAndDrag = false,
        ...propsToPass
      } = this.props;
      const { width, height } = this.props.dimensions || {};
      propsToPass.width = width;
      propsToPass.height = height - tabHeight;
      const selectedBps = getSequenceWithinRange(
        selectionLayer,
        sequenceData.sequence
      );
      if (!disableEditorClickAndDrag) {
        propsToPass = {
          ...propsToPass,
          selectionLayerRightClicked: this.selectionLayerRightClicked,
          digestLaneRightClicked: this.digestLaneRightClicked,
          searchLayerRightClicked: this.searchLayerRightClicked,
          backgroundRightClicked: this.backgroundRightClicked,
          featureRightClicked: this.featureRightClicked,
          partRightClicked: this.partRightClicked,
          primerRightClicked: this.primerRightClicked,
          featureDoubleClicked: this.featureDoubleClicked,
          partDoubleClicked: this.partDoubleClicked,
          primerDoubleClicked: this.primerDoubleClicked,
          warningDoubleClicked: this.warningDoubleClicked,
          warningRightClicked: this.warningRightClicked,
          orfRightClicked: this.orfRightClicked,
          deletionLayerRightClicked: this.deletionLayerRightClicked,
          cutsiteRightClicked: this.cutsiteRightClicked,
          cutsiteDoubleClicked: this.cutsiteDoubleClicked,
          translationRightClicked: this.translationRightClicked,
          ...annotationClickHandlers.reduce((acc, handler) => {
            acc[handler] = this[handler];
            return acc;
          }, {}),
          editorClicked: (p) => {
            editorClicked({
              ...p,
              updateSelectionOrCaret: this.updateSelectionOrCaret
            });
          }
        };
      }
      return /* @__PURE__ */ jsxs(
        "div",
        {
          tabIndex: 0,
          ref: (c) => this.node = c,
          className: "veVectorInteractionWrapper",
          style: { position: "relative", ...vectorInteractionWrapperStyle },
          onFocus: this.handleWrapperFocus,
          children: [
            closePanelButton,
            /* @__PURE__ */ jsx(
              Keyboard,
              {
                value: selectedBps,
                onCopy: this.handleCopy,
                onPaste: this.handlePaste,
                onCut: this.handleCut
              }
            ),
            /* @__PURE__ */ jsx(Component, { ...propsToPass }),
            this.state.isPasting && /* @__PURE__ */ jsx(
              "div",
              {
                className: "vePasteLoadingOverlay",
                style: {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1e4
                },
                children: /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    },
                    children: [
                      /* @__PURE__ */ jsx(Spinner, { size: 50, intent: Intent.PRIMARY }),
                      /* @__PURE__ */ jsx("h3", { style: { marginTop: 20 }, children: "Pasting sequence..." })
                    ]
                  }
                )
              }
            )
          ]
        }
      );
    }
  };
}
const withEditorInteractions = compose(
  withStore,
  withEditorProps,
  branch(({ noInteractions }) => !noInteractions, VectorInteractionHOC)
);
export default withEditorInteractions;
function getGenbankFromSelection(selectedSeqData, sequenceData) {
  const spansEntireSeq = sequenceData.sequence.length === selectedSeqData.sequence.length;
  const feats = map(selectedSeqData.features);
  const just1Feat = feats.length === 1;
  return {
    ...selectedSeqData,
    textToCopy: jsonToGenbank({
      ...selectedSeqData,
      name: spansEntireSeq ? selectedSeqData.name : just1Feat ? feats[0].name : selectedSeqData.name + "_partial",
      circular: spansEntireSeq ? selectedSeqData.circular : false
    })
  };
}
const insertAndSelectHelper = async ({ seqDataToInsert, props }) => {
  const {
    updateSequenceData,
    wrappedInsertSequenceDataAtPositionOrRange,
    sequenceData,
    selectionLayerUpdate,
    caretPosition,
    selectionLayer,
    bpLimit
  } = props;
  const [newSeqData, { maintainOriginSplit, abortSeqChange }] = await wrappedInsertSequenceDataAtPositionOrRange(
    seqDataToInsert,
    sequenceData,
    caretPosition > -1 ? caretPosition : selectionLayer
  );
  if (abortSeqChange) {
    throw new Error("abortSeqChange");
  }
  if (bpLimit) {
    if (newSeqData.sequence.length > bpLimit) {
      window.toastr.error(
        `Sorry, you cannot go over the limit of ${bpLimit} base pairs`
      );
      throw new Error("bpLimit exceeded");
    }
  }
  if (caretPosition <= -1 && selectionLayer.start > -1 && seqDataToInsert.sequence !== void 0) {
    const seqToReplace = getSequenceWithinRange(
      selectionLayer,
      sequenceData.sequence
    );
    const insertedSeq = seqDataToInsert.sequence || "";
    if (seqToReplace && seqToReplace.toUpperCase() !== insertedSeq.toUpperCase()) {
      const mutationName = `${seqToReplace.toUpperCase()}>${insertedSeq.toUpperCase()}`;
      const seqDataInsertLength2 = insertedSeq.length;
      const mutationStart = selectionLayer.start;
      const mutationEnd = mutationStart + seqDataInsertLength2 - 1;
      const mutationFeature = {
        id: "mutation_" + Math.random().toString(36).substr(2, 9),
        name: mutationName,
        type: "s_mutation",
        start: mutationStart,
        end: mutationEnd >= mutationStart ? mutationEnd % newSeqData.sequence.length : mutationStart,
        forward: true
      };
      if (Array.isArray(newSeqData.features)) {
        newSeqData.features = [...newSeqData.features, mutationFeature];
      } else if (newSeqData.features && typeof newSeqData.features === "object") {
        newSeqData.features = {
          ...newSeqData.features,
          [mutationFeature.id]: mutationFeature
        };
      } else {
        newSeqData.features = [mutationFeature];
      }
    }
  }
  updateSequenceData(newSeqData);
  const seqDataInsertLength = seqDataToInsert.sequence ? seqDataToInsert.sequence.length : null;
  const selectionStartDistanceFromEnd = Math.min(sequenceData.size - selectionLayer.start, seqDataInsertLength) || seqDataInsertLength;
  const newSelectionLayerStart = caretPosition > -1 ? caretPosition : selectionLayer.start > selectionLayer.end ? maintainOriginSplit ? newSeqData.size - selectionStartDistanceFromEnd : 0 : selectionLayer.start;
  const newSelectionLayerEnd = newSelectionLayerStart + (seqDataToInsert.sequence ? seqDataToInsert.sequence.length - 1 : seqDataToInsert.proteinSequence ? seqDataToInsert.proteinSequence.length * 3 - 1 : 0);
  selectionLayerUpdate({
    start: newSelectionLayerStart,
    end: newSelectionLayerEnd % newSeqData.sequence.length
  });
};
function getEditDeleteHandlers(type, annotation) {
  return [
    ...annotation.isEditLocked ? [
      {
        shouldDismissPopover: false,
        text: /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              fontSize: 11,
              fontStyle: "italic",
              color: "rgba(0,0,0,.5)"
            },
            children: typeof annotation.isEditLocked === "string" ? annotation.isEditLocked : `Note: This Annotation is Locked`
          }
        )
      }
    ] : [],
    `edit${type}`,
    ...annotation.isEditLocked ? [] : [`delete${type}`]
  ];
}
